import { LEARN } from 'constants/actionTypes';
import { receiveContents } from 'actions/learn/contents';
import attempt from 'utils/attempt';
import supportsLocalStorage from 'utils/supportsLocalStorage';

/**
 * The version of the current cache implementation.
 *
 * This is used to easily invalidate the cache if we introduce any breaking
 * changes to the implementation logic that could potentially causes issues if
 * we feed it old data that does not match what's expecting.
 */
const VERSION = 1;

/**
 * A middleware that handles caching the learn dashboard request payload.
 *
 * This is used an optimization to speed up the loading time of the page.
 *
 * When the page dispatches a "request" action, this middleware will attempt to
 * deliver the payload stored in cache (local-storage). When the request
 * finishes and dispatches a "contents received" action, this middleware will
 * update the cache with latest action payload (the request response).
 *
 * This middleware is written with the idea that it can be removed at any point
 * with very minimal-to-no code changes to the rest of the learn page. Please
 * try to scope all the caching logic into this middleware as much as possible.
 */
export const cacheMiddleware = store => next => action => {
  const { meta } = store.getState();

  if (
    !supportsLocalStorage ||
    !meta.features.combined_learner_experience_browser_cache
  ) {
    // Skip the middleware
    return next(action);
  }

  // The key of cache data. It's scoped by user ID in case multiple users logged
  // into the same company account using the same browser
  const CACHE_KEY = `learn-cache-v${VERSION}_${meta.currentUser.id}`;

  // Handling the request action (dispatched when the request begins)
  if (action.type === LEARN.CONTENTS.REQUEST) {
    const result = next(action);
    // let's try to serve what's store in cache
    const cachedPayload = attempt(() =>
      JSON.parse(localStorage.getItem(CACHE_KEY)),
    );
    if (cachedPayload) {
      next(receiveContents(cachedPayload));
    }
    return result;
  }

  // Handling the contents receive action (dispatched when the request finishes)
  if (action.type === LEARN.CONTENTS.RECEIVE) {
    const { type, ...payload } = action;
    attempt(() => localStorage.setItem(CACHE_KEY, JSON.stringify(payload)));
  }

  return next(action);
};
